// pages/TaskManagement/TaskAuditor.js
//
// Legacy: TasksAuditor.vb — org-wide per-employee task metrics, visible
// only to the "Task Auditor" permission (the role that replaced legacy
// hardcoded empids 2726/2706).
// Round TM-13 (2026) — audit Deliverable 13.
// Backend: GET /api/TaskManagement/task-auditor?from=&to=

import React, { useCallback, useEffect, useState } from "react";
import { Container, Card, CardBody, Badge, Alert, Button, Table, Input, Label } from "reactstrap";
import { DashboardPageHeader, EmptyState, TableSkeleton } from "../../Components/Common/DashboardKit";
import exportRowsToCsv from "../../Components/Common/DashboardKit/exportCsv";
import api from "../../api";

// Go-live parity (2026-09-24): LOCAL calendar date — toISOString() is the UTC
// date, so after ~8 pm Eastern "today" became tomorrow.
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const monthStart = () => { const d = new Date(); d.setDate(1); return iso(d); };

const TaskAuditor = () => {
    document.title = "Task Auditor || SSW Technologies Dashboard";

    const [from, setFrom] = useState(monthStart());
    const [to, setTo] = useState(iso(new Date()));
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");

    const load = useCallback(async (f, t) => {
        try {
            setLoading(true);
            setError("");
            const res = await api.get("/TaskManagement/task-auditor", {
                params: { from: f, to: t },
            });
            const data = res?.data ?? res;
            setRows(Array.isArray(data?.items) ? data.items : []);
        } catch (err) {
            setRows([]);
            setError(err?.response?.data?.error || err?.response?.data?.message
                || "Unable to load auditor metrics.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(from, to); }, [from, to, load]);

    const exportCsv = () => exportRowsToCsv(
        `task-auditor-${from}-${to}`,
        rows.map((r) => ({
            EmpID: r.empId,
            Name: r.name,
            HereToday: r.hereToday ? "Yes" : "No",
            TasksLogged: r.tasksLogged,
            Entries: r.entries,
            Hours: r.hours,
            Completed: r.completed,
            Overdue: r.overdue,
            AvgDaysOverdue: r.avgDaysOverdue,
            InvalidEntries: r.invalidEntries,
        }))
    );

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-search-eye-line"
                    title="Task Auditor"
                    subtitle="Per-employee task activity across the organization"
                    tone="default"
                    breadcrumb={[
                        { label: "Task Management", to: "/task-management" },
                        { label: "Task Auditor" },
                    ]}
                />

                {error && <Alert color="danger">{error}</Alert>}

                <Card className="shadow-sm border-0">
                    <CardBody>
                        <div className="d-flex flex-wrap align-items-end gap-3 mb-3">
                            <div>
                                <Label className="form-label mb-1">From</Label>
                                <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ width: "170px" }} />
                            </div>
                            <div>
                                <Label className="form-label mb-1">To</Label>
                                <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ width: "170px" }} />
                            </div>
                            <div className="ms-auto d-flex align-items-center gap-2">
                                <Badge color="light" className="text-muted">{rows.length} employees</Badge>
                                <Button size="sm" color="light" onClick={exportCsv} disabled={rows.length === 0}>
                                    <i className="ri-download-2-line align-bottom me-1"></i>CSV
                                </Button>
                            </div>
                        </div>

                        {loading ? (
                            <TableSkeleton rows={10} cols={9} />
                        ) : rows.length === 0 ? (
                            <EmptyState icon="ri-user-search-line" title="No data" message="No active employees found." />
                        ) : (
                            <div className="table-responsive">
                                <Table hover size="sm" className="align-middle mb-0">
                                    <thead className="table-light">
                                        <tr>
                                            <th>Employee</th>
                                            <th className="text-center">Here Today</th>
                                            <th className="text-end">Tasks Logged</th>
                                            <th className="text-end">Entries</th>
                                            <th className="text-end">Hours</th>
                                            <th className="text-end">Completed</th>
                                            <th className="text-end">Overdue</th>
                                            <th className="text-end">Avg Days Overdue</th>
                                            <th className="text-end">Invalid Entries</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((r) => (
                                            <tr key={r.empId} className={r.invalidEntries > 0 ? "table-warning" : ""}>
                                                <td className="fw-semibold">{r.name} <span className="text-muted">({r.empId})</span></td>
                                                <td className="text-center">
                                                    {r.hereToday
                                                        ? <i className="ri-checkbox-circle-fill text-success"></i>
                                                        : <i className="ri-close-circle-line text-muted"></i>}
                                                </td>
                                                <td className="text-end">{r.tasksLogged}</td>
                                                <td className="text-end">{r.entries}</td>
                                                <td className="text-end">{r.hours}</td>
                                                <td className="text-end">{r.completed}</td>
                                                <td className="text-end">{r.overdue > 0
                                                    ? <span className="text-danger fw-semibold">{r.overdue}</span> : 0}</td>
                                                <td className="text-end">{r.avgDaysOverdue}</td>
                                                <td className="text-end">{r.invalidEntries}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default TaskAuditor;
